import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiClock, FiCheck, FiX, FiMapPin } from 'react-icons/fi';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import './ClientCalendar.css';

const ClientCalendar = () => {
  const { currentUser, userProfile } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Fetch calendar events for the client
  useEffect(() => {
    if (!currentUser) return;

    const eventsRef = collection(db, 'calendarEvents');
    const q = query(
      eventsRef,
      where('clientId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date.toDate ? doc.data().date.toDate() : new Date(doc.data().date)
      }));
      setCalendarEntries(events);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getEventsForDate = (date) => {
    return calendarEntries.filter(entry => {
      const entryDate = entry.date;
      return (
        entryDate.getFullYear() === date.getFullYear() &&
        entryDate.getMonth() === date.getMonth() &&
        entryDate.getDate() === date.getDate()
      );
    });
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateClick = (date, events) => {
    if (events.length > 0) {
      setSelectedEvent(events[0]);
      setShowEventDetails(true);
    }
  };

  const handleUpdateAttendance = async (eventId, field, value) => {
    setUpdating(true);
    try {
      const eventRef = doc(db, 'calendarEvents', eventId);
      await updateDoc(eventRef, {
        [field]: value,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating attendance:', error);
    } finally {
      setUpdating(false);
    }
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Generate calendar days
  const calendarDays = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Get event type label
  const getEventTypeLabel = (type) => {
    const labels = {
      'session': 'Training Session',
      'check-in': 'Check-in',
      'meal-plan-review': 'Meal Plan Review',
      'progress-check': 'Progress Check',
      'consultation': 'Consultation',
      'other': 'Event'
    };
    return labels[type] || 'Event';
  };

  return (
    <div className="client-calendar-section">
      {/* Calendar Header */}
      <div className="client-calendar-header">
        <div className="header-left">
          <h2>My Schedule</h2>
          <p className="calendar-subtitle">
            {calendarEntries.length} event{calendarEntries.length !== 1 ? 's' : ''} scheduled
          </p>
        </div>
        {userProfile?.trainerId && (
          <div className="trainer-badge">
            <span>Coach: {userProfile.trainerName || 'Your Trainer'}</span>
          </div>
        )}
      </div>

      {/* Month Navigation */}
      <div className="month-navigation">
        <button className="nav-btn" onClick={handlePreviousMonth}>
          <FiChevronLeft />
        </button>
        <h3 className="month-year">{monthNames[month]} {year}</h3>
        <button className="nav-btn" onClick={handleNextMonth}>
          <FiChevronRight />
        </button>
      </div>

      {/* Day Names */}
      <div className="calendar-days-header">
        {dayNames.map(day => (
          <div key={day} className="day-name">{day}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="client-calendar-grid">
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="calendar-day empty"></div>;
          }

          const date = new Date(year, month, day);
          const events = getEventsForDate(date);
          const isToday =
            date.getDate() === new Date().getDate() &&
            date.getMonth() === new Date().getMonth() &&
            date.getFullYear() === new Date().getFullYear();

          return (
            <div
              key={day}
              className={`calendar-day ${isToday ? 'today' : ''} ${events.length > 0 ? 'has-events' : ''}`}
              onClick={() => handleDateClick(date, events)}
            >
              <div className="day-number">{day}</div>
              {events.length > 0 && (
                <div className="day-events">
                  {events.slice(0, 2).map(event => (
                    <div
                      key={event.id}
                      className={`event-item event-${event.type} ${event.confirmed ? 'confirmed' : ''} ${event.cancelled ? 'cancelled' : ''}`}
                    >
                      <FiClock className="event-icon" />
                      <span className="event-time">{event.startTime}</span>
                      <span className="event-title">{event.title}</span>
                      {event.confirmed && <FiCheck className="status-icon confirmed" />}
                      {event.cancelled && <FiX className="status-icon cancelled" />}
                    </div>
                  ))}
                  {events.length > 2 && (
                    <div className="more-events">+{events.length - 2} more</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Event Details Modal */}
      {showEventDetails && selectedEvent && (
        <div className="modal-overlay" onClick={() => setShowEventDetails(false)}>
          <div className="event-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="event-type-badge">{getEventTypeLabel(selectedEvent.type)}</div>
              <button className="modal-close" onClick={() => setShowEventDetails(false)}>
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <h3 className="event-title-large">{selectedEvent.title}</h3>

              <div className="event-info-grid">
                <div className="info-item">
                  <FiClock className="info-icon" />
                  <div>
                    <div className="info-label">Date & Time</div>
                    <div className="info-value">
                      {selectedEvent.date.toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="info-value">
                      {selectedEvent.startTime} - {selectedEvent.endTime}
                    </div>
                  </div>
                </div>

                {selectedEvent.location && (
                  <div className="info-item">
                    <FiMapPin className="info-icon" />
                    <div>
                      <div className="info-label">Location</div>
                      <div className="info-value">{selectedEvent.location}</div>
                    </div>
                  </div>
                )}

                {selectedEvent.description && (
                  <div className="info-item full-width">
                    <div className="info-label">Details</div>
                    <div className="info-value">{selectedEvent.description}</div>
                  </div>
                )}
              </div>

              {/* Attendance Status */}
              <div className="attendance-section">
                <h4>Attendance Status</h4>
                <div className="attendance-options">
                  <button
                    className={`attendance-btn ${selectedEvent.confirmed ? 'active confirmed' : ''}`}
                    onClick={() => handleUpdateAttendance(selectedEvent.id, 'confirmed', !selectedEvent.confirmed)}
                    disabled={updating || selectedEvent.cancelled}
                  >
                    <FiCheck />
                    {selectedEvent.confirmed ? 'Confirmed' : 'Confirm Attendance'}
                  </button>

                  <button
                    className={`attendance-btn ${selectedEvent.cancelled ? 'active cancelled' : ''}`}
                    onClick={() => handleUpdateAttendance(selectedEvent.id, 'cancelled', !selectedEvent.cancelled)}
                    disabled={updating || selectedEvent.confirmed}
                  >
                    <FiX />
                    {selectedEvent.cancelled ? 'Cancelled' : 'Cancel'}
                  </button>
                </div>

                {selectedEvent.confirmed && !selectedEvent.cancelled && (
                  <div className="status-message confirmed">
                    <FiCheck /> You've confirmed this session
                  </div>
                )}

                {selectedEvent.cancelled && (
                  <div className="status-message cancelled">
                    <FiX /> This session has been cancelled
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowEventDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Sessions List */}
      <div className="upcoming-sessions-list">
        <h3>Upcoming Sessions</h3>
        {calendarEntries
          .filter(event => event.date >= new Date())
          .sort((a, b) => a.date - b.date)
          .slice(0, 5)
          .map(event => (
            <div
              key={event.id}
              className={`session-card ${event.confirmed ? 'confirmed' : ''} ${event.cancelled ? 'cancelled' : ''}`}
              onClick={() => {
                setSelectedEvent(event);
                setShowEventDetails(true);
              }}
            >
              <div className="session-date">
                <div className="date-badge">
                  <div className="month">{event.date.toLocaleDateString('en-US', { month: 'short' })}</div>
                  <div className="day">{event.date.getDate()}</div>
                </div>
              </div>
              <div className="session-details">
                <h4>{event.title}</h4>
                <div className="session-meta">
                  <span><FiClock /> {event.startTime} - {event.endTime}</span>
                  {event.location && <span><FiMapPin /> {event.location}</span>}
                </div>
                <span className="session-type">{getEventTypeLabel(event.type)}</span>
              </div>
              <div className="session-status">
                {event.confirmed && <span className="status-badge confirmed"><FiCheck /> Confirmed</span>}
                {event.cancelled && <span className="status-badge cancelled"><FiX /> Cancelled</span>}
                {!event.confirmed && !event.cancelled && <span className="status-badge pending">Pending</span>}
              </div>
            </div>
          ))}

        {calendarEntries.filter(event => event.date >= new Date()).length === 0 && (
          <div className="no-sessions">
            <p>No upcoming sessions scheduled</p>
            <span>Your trainer will schedule sessions for you</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientCalendar;
